import React, { useState, useEffect } from "react";
import { Megaphone, Cloud, Shield, Tag, Info, Clock, Check, ChevronRight } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { 
  getAnnouncementsForVendor, 
  markAnnouncementAsRead 
} from "../../../firebaseFunctions/announcements/announcementService";
import { getEventDetails } from "../../../firebaseFunctions/getEventDetails/getEventDetails";
import ModalLoadingState from "../../PopUpModal/ModalLoadingState";

const categoryConfig = {
  general: { icon: Info, color: "bg-blue-100 text-blue-700 border-blue-200", label: "General" },
  weather: { icon: Cloud, color: "bg-yellow-100 text-yellow-700 border-yellow-200", label: "Weather" },
  security: { icon: Shield, color: "bg-red-100 text-red-700 border-red-200", label: "Security" },
  promotion: { icon: Tag, color: "bg-green-100 text-green-700 border-green-200", label: "Promotion" },
};

const formatDate = (date) => {
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined
  });
};

const AnnouncementCard = ({ announcement, eventName, onMarkAsRead }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const category = categoryConfig[announcement.category] || categoryConfig.general;
  const CategoryIcon = category.icon;

  const handleClick = async () => {
    setIsExpanded(!isExpanded);
    if (!announcement.isRead) {
      await onMarkAsRead(announcement.id);
    }
  };

  return (
    <div 
      className={`border rounded-xl overflow-hidden transition-all cursor-pointer ${
        announcement.isRead 
          ? "bg-white border-grey-200 hover:border-grey-300" 
          : "bg-blue-50 border-blue-200 hover:border-blue-300"
      }`}
      onClick={handleClick}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${category.color.split(' ').slice(0, 2).join(' ')}`}>
            <CategoryIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${category.color}`}>
                {category.label}
              </span>
              {!announcement.isRead && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500 text-white">
                  New
                </span>
              )}
            </div>
            <h4 className="font-semibold text-grey-900 mb-1">{announcement.title}</h4>
            <div className="flex items-center gap-3 text-xs text-grey-500">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDate(announcement.createdAt)}
              </span>
              {eventName && (
                <span className="truncate">{eventName}</span>
              )}
            </div>
          </div>
          <ChevronRight className={`h-5 w-5 text-grey-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
        </div>
        
        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-grey-200">
            <p className="text-grey-700 whitespace-pre-wrap">{announcement.message}</p>
            {announcement.isRead && (
              <div className="mt-3 flex items-center gap-1 text-xs text-green-600">
                <Check className="h-3 w-3" />
                Read
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const AnnouncementsTab = () => {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [eventNames, setEventNames] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const loadAnnouncements = async () => {
    if (!user?.uid) return;
    
    setIsLoading(true);
    try {
      const result = await getAnnouncementsForVendor(user.uid);
      if (result.success) {
        setAnnouncements(result.announcements);
        
        const uniqueEventIds = [...new Set(result.announcements.map(a => a.eventId))];
        const names = {};
        
        for (const eventId of uniqueEventIds) {
          try {
            const eventDetails = await getEventDetails(eventId);
            if (eventDetails) {
              names[eventId] = eventDetails.eventName;
            }
          } catch (e) {
            console.error("Error fetching event name:", e);
          }
        }
        
        setEventNames(names);
      }
    } catch (error) {
      console.error("Error loading announcements:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAnnouncements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  const handleMarkAsRead = async (announcementId) => {
    if (!user?.uid) return;
    
    await markAnnouncementAsRead(announcementId, user.uid);
    setAnnouncements(prev => 
      prev.map(a => 
        a.id === announcementId ? { ...a, isRead: true } : a
      )
    );
  };

  const filteredAnnouncements = announcements.filter(a => {
    if (filter === "all") return true;
    if (filter === "unread") return !a.isRead;
    return a.category === filter;
  });

  const unreadCount = announcements.filter(a => !a.isRead).length;

  if (isLoading) {
    return <ModalLoadingState />;
  }

  return (
    <div className="p-3 sm:p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Megaphone className="h-6 w-6 text-grey-700" />
          <h1 className="text-2xl font-bold text-grey-900">Announcements</h1>
          {unreadCount > 0 && (
            <span className="px-2.5 py-0.5 bg-blue-500 text-white text-sm font-medium rounded-full">
              {unreadCount} new
            </span>
          )}
        </div>
        <p className="text-grey-600">Updates from event organizers for events you're registered to</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: "all", label: "All" },
          { id: "unread", label: `Unread (${unreadCount})` },
          { id: "weather", label: "Weather" },
          { id: "security", label: "Security" },
          { id: "promotion", label: "Promotions" },
          { id: "general", label: "General" },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              filter === f.id
                ? "bg-grey-800 text-white"
                : "bg-grey-100 text-grey-600 hover:bg-grey-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {announcements.length === 0 ? (
        <div className="text-center py-16 bg-grey-50 rounded-xl border border-grey-200">
          <div className="mx-auto w-16 h-16 bg-grey-100 rounded-full flex items-center justify-center mb-4">
            <Megaphone className="h-8 w-8 text-grey-400" />
          </div>
          <h3 className="text-lg font-medium text-grey-900 mb-2">No announcements yet</h3>
          <p className="text-grey-500 max-w-md mx-auto">
            When event organizers send updates about weather, security, or promotions, 
            you'll see them here.
          </p>
        </div>
      ) : filteredAnnouncements.length === 0 ? (
        <div className="text-center py-12 bg-grey-50 rounded-xl border border-grey-200">
          <p className="text-grey-500">No announcements match this filter</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredAnnouncements.map((announcement) => (
            <AnnouncementCard
              key={announcement.id}
              announcement={announcement}
              eventName={eventNames[announcement.eventId]}
              onMarkAsRead={handleMarkAsRead}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AnnouncementsTab;
